import { CheckCircle, RefreshCw, MinusCircle, Trash2, Sparkles, AlertCircle, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/core/components/ui/sheet';
import { Button } from '@/core/components/ui/button';
import { cn } from '@/core/lib/utils';
import { useDataErrorStore, type AnalyzeGroupEntry } from '../../stores/data-error-store';
import type { HandleType } from '../../types/data-error-types';

const CONFIDENCE_LABEL: Record<string, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const CONFIDENCE_CLS: Record<string, string> = {
  high: 'bg-amber-100 text-amber-800',
  medium: 'bg-blue-100 text-blue-800',
  low: 'bg-gray-100 text-gray-600',
};

const ACTION_CLS: Record<HandleType, string> = {
  Approve: 'text-green-700',
  Replacement: 'text-blue-700',
  Ignore: 'text-gray-600',
  Delete: 'text-red-600',
  ManualCorrection: 'text-purple-700',
};

const ACTION_ICON: Record<HandleType, React.ReactNode> = {
  Approve: <CheckCircle className="h-3.5 w-3.5" />,
  Replacement: <RefreshCw className="h-3.5 w-3.5" />,
  Ignore: <MinusCircle className="h-3.5 w-3.5" />,
  Delete: <Trash2 className="h-3.5 w-3.5" />,
  ManualCorrection: <Sparkles className="h-3.5 w-3.5" />,
};

function EntryRow({ entry, checked, onToggle, onPreview }: {
  entry: AnalyzeGroupEntry;
  checked: boolean;
  onToggle: () => void;
  onPreview: () => void;
}) {
  const canPreview = !entry.isLoading && !entry.error && entry.meterValues.length > 0;

  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 border-b border-[#E8E8E8] last:border-0 transition-colors',
        checked ? 'bg-[#FFFBEB]' : 'bg-white',
        canPreview && 'cursor-pointer hover:bg-[#F9F9F9]'
      )}
      onClick={canPreview ? onPreview : undefined}
    >
      {/* Checkbox */}
      <div className="pt-0.5 shrink-0" onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-[#E8E8E8] accent-[#FFCD00]"
          checked={checked}
          disabled={entry.isLoading || entry.suggestion === null}
          onChange={onToggle}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-mono text-xs text-[#606060]">#{entry.item.irregularId}</span>
          <span className="text-xs text-[#909090]">Connector {entry.item.connectorId}</span>
          <span className={cn('text-xs font-mono', entry.item.delta < 0 ? 'text-red-600 font-semibold' : 'text-[#606060]')}>
            Δ {entry.item.delta.toFixed(3)} kWh
          </span>
        </div>

        {entry.isLoading && (
          <div className="flex items-center gap-1.5 text-xs text-[#909090]">
            <div className="h-3 w-3 rounded-full border border-gray-300 border-t-[#FFCD00] animate-spin" />
            Analyzing…
          </div>
        )}

        {!entry.isLoading && entry.error && (
          <div className="flex items-center gap-1 text-xs text-red-500">
            <AlertCircle className="h-3 w-3" />
            Failed to load meter values
          </div>
        )}

        {!entry.isLoading && !entry.error && entry.suggestion && (
          <div className="space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <div className={cn('flex items-center gap-1 text-xs font-medium', ACTION_CLS[entry.suggestion.action])}>
                {ACTION_ICON[entry.suggestion.action]}
                {entry.suggestion.action}
              </div>
              <span className={cn('inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium', CONFIDENCE_CLS[entry.suggestion.confidence])}>
                {CONFIDENCE_LABEL[entry.suggestion.confidence]}
              </span>
            </div>
            <p className="text-xs text-[#606060] leading-snug">{entry.suggestion.title}</p>
          </div>
        )}

        {!entry.isLoading && !entry.error && !entry.suggestion && (
          <p className="text-xs text-[#909090] italic">No pattern detected — manual review</p>
        )}
      </div>

      {/* Drill-in indicator */}
      {canPreview && (
        <ChevronRight className="h-4 w-4 text-[#C0C0C0] shrink-0 self-center" />
      )}
    </div>
  );
}

export function AnalyzeAllBlade() {
  const {
    analyzeGroupEntries,
    analyzeGroupChecked,
    isApplyingGroupFix,
    groupAnalysisError,
    toggleGroupAnalysisCheck,
    setGroupAnalysisAllChecked,
    applyGroupAnalysis,
    closeGroupAnalysis,
    previewItem,
  } = useDataErrorStore();

  const open = analyzeGroupEntries.length > 0;
  if (!open) return null;

  const machineName = analyzeGroupEntries[0]?.item.machineName ?? 'Charger';
  const machineId = analyzeGroupEntries[0]?.item.machineId;

  const isAnyLoading = analyzeGroupEntries.some(e => e.isLoading);
  const withSuggestion = analyzeGroupEntries.filter(e => e.suggestion !== null);
  const checkedCount = analyzeGroupEntries.filter(e => analyzeGroupChecked.has(e.item.irregularId)).length;
  const allWithSuggestionChecked = withSuggestion.length > 0 &&
    withSuggestion.every(e => analyzeGroupChecked.has(e.item.irregularId));

  return (
    <Sheet open={open} onOpenChange={isOpen => { if (!isOpen) closeGroupAnalysis(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-hidden flex flex-col p-0">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#E8E8E8] shrink-0">
          <div className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <div>
              <SheetTitle className="text-base font-semibold text-[#262626]">
                Analyze All — {machineName}
              </SheetTitle>
              <SheetDescription className="text-xs text-[#606060] mt-0.5">
                #{machineId} · {analyzeGroupEntries.length} pending record{analyzeGroupEntries.length !== 1 ? 's' : ''}
                {isAnyLoading ? ' · Analyzing…' : ` · ${withSuggestion.length} suggestion${withSuggestion.length !== 1 ? 's' : ''} found`}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Select all / deselect all */}
        {!isAnyLoading && withSuggestion.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2 bg-[#FAFAFA] border-b border-[#E8E8E8] shrink-0">
            <span className="text-xs text-[#606060]">
              {checkedCount} of {withSuggestion.length} selected
            </span>
            <button
              className="ml-auto text-xs text-[#262626] hover:underline font-medium"
              onClick={() => setGroupAnalysisAllChecked(!allWithSuggestionChecked)}
            >
              {allWithSuggestionChecked ? 'Deselect all' : 'Select all'}
            </button>
          </div>
        )}

        {/* Entry list */}
        <div className="flex-1 overflow-y-auto">
          {analyzeGroupEntries.map(entry => (
            <EntryRow
              key={entry.item.irregularId}
              entry={entry}
              checked={analyzeGroupChecked.has(entry.item.irregularId)}
              onToggle={() => toggleGroupAnalysisCheck(entry.item.irregularId)}
              onPreview={() => previewItem(entry.item, entry.meterValues)}
            />
          ))}
        </div>

        {/* Footer — apply */}
        <div className="shrink-0 px-6 py-4 border-t border-[#E8E8E8] bg-white space-y-2">
          {groupAnalysisError && (
            <div className="flex items-center gap-1.5 text-xs text-red-600">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {groupAnalysisError}
            </div>
          )}
          {checkedCount === 0 ? (
            <p className="text-xs text-[#909090] text-center">
              {isAnyLoading ? 'Waiting for analysis to complete…' : 'Select records to apply fixes'}
            </p>
          ) : (
            <>
              {isAnyLoading && (
                <p className="text-xs text-[#909090] text-center">
                  {checkedCount} fix{checkedCount !== 1 ? 'es' : ''} ready — waiting for remaining analyses…
                </p>
              )}
              <Button
                className="w-full bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626] font-semibold h-9"
                disabled={isApplyingGroupFix || isAnyLoading}
                onClick={async () => {
                  await applyGroupAnalysis();
                  const state = useDataErrorStore.getState();
                  if (!state.groupAnalysisError) {
                    toast.success(`${checkedCount} fix${checkedCount !== 1 ? 'es' : ''} applied`);
                  }
                }}
              >
                {isApplyingGroupFix
                  ? 'Applying…'
                  : `Apply ${checkedCount} fix${checkedCount !== 1 ? 'es' : ''}`}
              </Button>
              <Button
                variant="outline"
                className="w-full h-9 text-red-600 border-red-200 hover:bg-red-50 font-semibold text-sm"
                disabled={isApplyingGroupFix || isAnyLoading}
                onClick={async () => {
                  await applyGroupAnalysis('Delete');
                  const state = useDataErrorStore.getState();
                  if (!state.groupAnalysisError) {
                    toast.success(`${checkedCount} record${checkedCount !== 1 ? 's' : ''} deleted`);
                  }
                }}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                {isApplyingGroupFix ? 'Applying…' : `Delete ${checkedCount} selected`}
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
